/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef } from 'react'; 
import './FilledInput.css';
import tooltipIcon from '../assets/tooltip.svg';
import securityIcon from '../assets/security.svg';

const cardPatterns = {
  visa: /^4\d{0,15}/,
  mastercard: /^(5[1-5]|2[2-7])\d{0,14}/,
  amex: /^3[47]\d{0,13}/,
  discover: /^6(?:011|5\d{2})\d{0,12}/,
  diners: /^3(?:0[0-5]|[68])\d{0,11}/,
  jcb: /^(?:2131|1800|35\d{3})\d{0,11}/,
};

function detectCardType(number: any) {
  const sanitized = number.replace(/\D/g, '');
  for (const [type, pattern] of Object.entries(cardPatterns)) {
    if (pattern.test(sanitized)) return type;
  }
  return 'unknown';
}

function formatCardNumber(number: any) {
  const sanitized = number.replace(/\D/g, '');
  const cardType = detectCardType(sanitized);

  let parts;

  switch (cardType) {
    case 'amex':
      parts = sanitized.match(/^(\d{1,4})(\d{1,6})?(\d{1,5})?$/);
      return parts ? [parts[1], parts[2], parts[3]].filter(Boolean).join(' ') : sanitized;

    case 'diners':
      parts = sanitized.match(/^(\d{1,4})(\d{1,6})?(\d{1,4})?$/);
      return parts ? [parts[1], parts[2], parts[3]].filter(Boolean).join(' ') : sanitized;

    default:
      parts = sanitized.match(/(\d{1,4})/g);
      return parts ? parts.join(' ') : sanitized;
  }
}

function calculateCursorPosition(oldVal: any, newVal: any, oldCursor: any) {
  let rawIndex = 0;
  for (let i = 0; i < oldCursor; i++) {
    if (/\d/.test(oldVal[i])) rawIndex++;
  }

  let newIndex = 0, count = 0;
  for (let i = 0; i < newVal.length; i++) {
    if (/\d/.test(newVal[i])) count++;
    if (count >= rawIndex) {
      newIndex = i + 1;
      break;
    }
  }

  return newIndex;
}

function formatExpDate(value: string){
  const sanitized = value.replace(/\D/g, '').slice(0, 4);
  if(sanitized.length < 3) {
    return sanitized;
  }
  const formatted = sanitized.replace(/(\d{2})(\d{0,2})/, '$1/$2');
  return formatted;
}

function formatExpiry(value: string) {
  const cleaned = value.replace(/\D+/g, '').slice(0, 4); // Only numbers, max 4 digits
  if (cleaned.length < 3) {
    return cleaned;
  }
  return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
}

interface FilledInputProps {
    label: string;
    id: string;
    placeholder?: string;
    errorMessage?: string;
    helperText?: string;
    validate?: (value: string) => string | null; // Validation function
    inputChange?: (e: any) => void;
    tooltip?: string; // Tooltip text for icons
    value?: string;
  }
  
  const FilledInput: React.FC<FilledInputProps> = ({
    label,
    id,
    errorMessage,
    helperText,
    validate,
    tooltip,
    inputChange,
    value: controlledValue,
  }) => {
    const [value, setValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if(inputChange){
        inputChange(e);
      }
      const raw = e.target.value;
      const cursor = e.target.selectionStart;
      if(id === 'expirationDate'){
        const digitsOnly = raw.replace(/\D/g, '');
        const formatted = formatExpDate(digitsOnly);
        const newCursor = calculateCursorPosition(raw, formatted, cursor);
        setValue(formatted);

        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newCursor, newCursor);
          }
        });
      } else if(id === 'cardNumber'){
        const digitsOnly = raw.replace(/\D/g, '');
        const formatted = formatCardNumber(digitsOnly);
  
        const newCursor = calculateCursorPosition(raw, formatted, cursor);
  
        setValue(formatted);
  
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newCursor, newCursor);
          }
        });
      } else {
        const newValue = e.target.value;
        setValue(newValue);
      }
    };
  
    const handleBlur = () => {
      if (validate && value.trim() !== '') {
        setError(validate(value));
      }
    };
  
    return (
      <div className={`input-wrapper ${error || errorMessage ? 'error' : ''}`}>
        <input
          type="text"
          id={id}
          className="filled-input"
          placeholder=" "
          value={controlledValue !== undefined ? controlledValue : value}
          onChange={handleChange}
          onBlur={handleBlur} // Validate on blur
          {...id === 'cardNumber' && { maxLength: 19 }}
          {...id === 'expirationDate' && { maxLength: 5 }}
          {...id === 'cardSecurityCode' && { maxLength: 4 }}
        />
        <label htmlFor={label} className="filled-label">
          {label}
        </label>
        
        {tooltip && (
          <div
            className="tooltip-icon"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
         <img src={tooltip && tooltip !== ' ' ? tooltipIcon : securityIcon} alt="info" className="info-icon" />
         {tooltip && tooltip !== ' ' && showTooltip && <div className="tooltip-message">{tooltip}</div>}
          </div>
        )}
  
        {/* Error or Helper Text */}
        {(error || errorMessage) && <p className="filled-error-text">{error || errorMessage}</p>}
        {!error && !errorMessage && helperText && <p className="filled-helper-text">{helperText}</p>}
      </div>
    );
  };

export default FilledInput;